import type { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import axios, { isAxiosError } from 'axios';
import { STORAGE_KEYS } from '@sulis-liiga/common';

// ---------------------------------------------------------------------------
// Tenant-slug module variable
//
// The axios request interceptor reads this synchronously to decide which
// tenant prefix to inject.  uiSlice dispatches `setCurrentTenantSlug`
// whenever the selected league changes; that action calls
// `setActiveTenantSlug()` exported here, mirroring the value into this
// module-level variable so the interceptor never has to import the Redux
// store (which would create a circular-dependency chain).
// ---------------------------------------------------------------------------

/** Currently selected tenant slug.  Empty string = no league selected yet. */
let _activeTenantSlug = '';

/**
 * Update the active tenant slug that the axios interceptor will use.
 * Call this whenever the selected league (and its tenantSlug) changes.
 */
export function setActiveTenantSlug(slug: string): void {
  _activeTenantSlug = slug;
}

/**
 * Read the active tenant slug (used by tests to inspect current state).
 */
export function getActiveTenantSlug(): string {
  return _activeTenantSlug;
}

// ---------------------------------------------------------------------------
// URL classification helpers
// ---------------------------------------------------------------------------

/**
 * Paths that must NEVER receive the tenant prefix and must always reach the
 * global (registry) server at `/api/v1/...`:
 *
 *   /v1/auth/*          — login, register, refresh, password reset, verify
 *   /v1/discovery/*     — cross-tenant league enumeration
 *   /v1/users/me*       — the logged-in player's profile lives in the registry
 *   /v1/users?*         — user search (registry)
 *   /v1/users/:id       — single user lookup (registry)
 */
const GLOBAL_PATH_PREFIXES = [
  '/v1/auth/',
  '/v1/discovery/',
  '/v1/users', // covers /v1/users/me, /v1/users/:id, /v1/users?q=...
  '/v1/teams/partners/search', // partner search reads the player registry (global)
];

function isGlobalPath(url: string): boolean {
  return GLOBAL_PATH_PREFIXES.some(prefix => url.startsWith(prefix));
}

// API base URL. In production this is the backend origin (e.g.
// https://api.sulis-liiga.fi/api) injected at build time; in dev it falls back
// to the same-origin `/api` proxy. Used by the axios instance below AND by the
// raw refresh-token call, which must target the API origin — not the current
// page origin — so token refresh works when the frontend and backend live on
// different subdomains.
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create an Axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 seconds timeout from nested implementation
});

// ---------------------------------------------------------------------------
// Request interceptor — auth token + tenant-path rewrite
// ---------------------------------------------------------------------------
api.interceptors.request.use(
  config => {
    // 1. Auth token
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 2. Tenant-path rewrite
    //
    // Tenant-scoped resources are addressed as:
    //   /api/<tenantSlug>/v1/<resource>
    //
    // The axios instance has baseURL=/api and calls pass paths like
    // `/v1/leagues/...`.  When there is an active tenant slug AND the
    // request is not a global path, we prepend `/<slug>` to the URL so
    // the final URL becomes `/api/<slug>/v1/...`.
    //
    // Global paths (/v1/auth/*, /v1/discovery/*, /v1/users*) are
    // intentionally excluded — they always hit the unprefixed route.
    const url = config.url ?? '';
    const tenantPrefix = `/${_activeTenantSlug}`;
    // Idempotent: skip if the URL is already tenant-prefixed. The response
    // interceptor retries the SAME config (with an already-rewritten url) after a
    // 401 token refresh; without this guard the retry would prepend the slug a
    // second time, producing `/<slug>/<slug>/v1/...` and a 404.
    if (_activeTenantSlug && !isGlobalPath(url) && !url.startsWith(`${tenantPrefix}/`)) {
      config.url = `${tenantPrefix}${url}`;
    }

    return config;
  },
  error => Promise.reject(error),
);

// Response interceptor for handling token expiration
api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Auth endpoints (login, refresh-token, register, password reset, verify) return
    // 401 for ordinary failures like a wrong password. These must NOT trigger the
    // token-refresh / redirect-to-login path below — doing so hard-reloads the page and
    // wipes the error message before the user can read it. Let the caller handle them.
    const isAuthEndpoint = !!originalRequest.url && originalRequest.url.includes('/auth/');

    // Check if the error is due to an expired token (401 error)
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        // Try to refresh the token
        const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
        if (!refreshToken) {
          // No refresh token => the visitor is not logged in. The app is
          // public-first, so a 401 from a background/public fetch is expected for
          // anonymous users and must NOT redirect to /login (that would break
          // browsing). Just reject and let the caller handle the empty result.
          // (Pages that require auth are gated by ProtectedRoute, which handles
          // the redirect itself.)
          return Promise.reject(error);
        }

        // Request new access token using refresh token
        const response = await axios.post<{ success: boolean; data: { accessToken: string } }>(
          `${API_BASE_URL}/v1/auth/refresh-token`,
          {
            refreshToken,
          },
        );

        // Update access token (backend returns ApiResponse wrapper)
        const accessToken = response.data.data?.accessToken;
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);

        // Retry the original request with the new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        return api(originalRequest);
      } catch (refreshError) {
        // If token refresh fails, logout the user
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

// Generic API call wrapper with type safety
export const apiCall = async <T>(
  method: 'get' | 'post' | 'put' | 'patch' | 'delete',
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> => {
  try {
    let response: AxiosResponse<{
      success: boolean;
      data: T;
      message?: string;
      error?: { message?: string; code?: string };
    }>;

    switch (method) {
      case 'get':
        response = await api.get(url, config);
        break;
      case 'post':
        response = await api.post(url, data, config);
        break;
      case 'put':
        response = await api.put(url, data, config);
        break;
      case 'patch':
        response = await api.patch(url, data, config);
        break;
      case 'delete':
        response = await api.delete(url, config);
        break;
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }

    // Check for API-level success flag
    if (response.data.success === false) {
      throw new Error(response.data.error?.message || 'API call failed');
    }

    return response.data.data;
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      // Extract API error message if available
      const errorMessage = error.response.data?.error?.message || error.message;
      throw new Error(errorMessage);
    }
    // Rethrow any other errors
    throw error;
  }
};

// API call variant that preserves pagination metadata
export const apiCallWithMeta = async <T>(
  method: 'get' | 'post' | 'put' | 'patch' | 'delete',
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<{
  data: T;
  meta?: { page?: number; limit?: number; total?: number; pages?: number };
}> => {
  try {
    let response: AxiosResponse<{
      success: boolean;
      data: T;
      meta?: { page?: number; limit?: number; total?: number; pages?: number };
      message?: string;
      error?: { message?: string; code?: string };
    }>;

    switch (method) {
      case 'get':
        response = await api.get(url, config);
        break;
      case 'post':
        response = await api.post(url, data, config);
        break;
      case 'put':
        response = await api.put(url, data, config);
        break;
      case 'patch':
        response = await api.patch(url, data, config);
        break;
      case 'delete':
        response = await api.delete(url, config);
        break;
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }

    if (response.data.success === false) {
      throw new Error(response.data.error?.message || 'API call failed');
    }

    return { data: response.data.data, meta: response.data.meta };
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      const errorMessage = error.response.data?.error?.message || error.message;
      throw new Error(errorMessage);
    }
    throw error;
  }
};

export default api;
